

























































import {
    computed,
    defineComponent,
    onBeforeUnmount,
    onMounted,
    ref,
} from '@vue/composition-api';
import { SdsButton } from '@superbet-group/web.lib.ui-components';
import {
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import authStore from '@store/modules/data/authentication/authenticationIndex';
import { ModalStore } from '@store/modules/ui/shared/modal/modalIndex';
import multiStepVerificationStoreIndex from '@store/modules/ui/shared/multiStepVerification/multiStepVerificationIndex';
import IdCardPendingKyc from '@accountFederation/modules/idCardVerification/components/idCardVerificationCaptive/IdCardPendingKyc.vue';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import { RouteName } from '@src/app/router/constants/RouteName';
import { captiveKycStore } from '@accountFederation/store/data/captiveKyc/captiveKycStore';
import { KycStatus } from '@accountFederation/modules/idCardVerification/getid/types';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { getIntermediaryStep } from '@shared/userProfile/idCardVerification/idCardVerificationEnterFlowState';
import VerificationPrompt from '@accountFederation/modules/idCardVerification/components/VerificationPrompt.vue';

export default defineComponent({
    name: 'IdCardVerificationCaptive',
    setup() {
        const isLoading = ref(true);
        const authModule = useVuexStore<typeof authStore>('data/authentication');
        const showSessionExpired = authModule.action('sessionExpired');
        const user = authModule.getter('user');
        const isFailed = computed(() => user.value?.kycDetails?.status === KycStatus.Failed);
        const shouldShowUnsuccessful = ref(false);

        const modalStore = useVuexStore<ModalStore>('ui/modal');
        const setModal = modalStore.action('setModal');
        const closeModal = modalStore.action('closeModal');

        const kycModule = useVuexStore<typeof multiStepVerificationStoreIndex>('ui/multiStepVerification');
        const idVerificationModalCode = kycModule.getter('idVerificationModalCode');
        const multiStepIdVerificationGroup = kycModule.getter('multiStepIdVerificationGroup');

        const captiveKycModule = useVuexStore<typeof captiveKycStore>('data/captiveKyc');
        const shouldVerifySms = captiveKycModule.getter('hasCaptivePhoneNumber');
        const freeFromCaptiveKyc = captiveKycModule.action('freeFromCaptiveKyc');
        const fetchKycDetails = captiveKycModule.action('fetchKycDetails');
        const hasCaptivePhoneNumber = captiveKycModule.getter('hasCaptivePhoneNumber');
        const isUnskippable = captiveKycModule.getter('isUnskippable');
        const hasAttemptedKycThisView = ref(false);

        onMounted(async () => {
            await getKycData();
        });

        AnalyticsSDK.getInstance().log('KYC_Intent', {
            eventLabel: 'Registration',
            status: user.value?.kycDetails?.status || KycStatus.Incomplete,
        });

        const isStatusPending = ref(user.value?.kycDetails?.status === KycStatus.NeedsReview);

        const getKycData = async () => {
            try {
                const user = authModule.getter('user');

                await fetchKycDetails();
                isLoading.value = false;
                switch (user.value?.kycDetails?.status) {
                    case KycStatus.Verified:
                        if (!shouldVerifySms.value) {
                            freeFromCaptiveKyc({
                                shouldRedirect: true,
                            });
                        }
                        shouldShowUnsuccessful.value = false;
                        isStatusPending.value = false;
                        break;
                    case KycStatus.Failed:
                        if (hasAttemptedKycThisView.value) {
                            AnalyticsSDK.getInstance().log('KYC_Unsuccessful');
                        }
                        shouldShowUnsuccessful.value = true;
                        isStatusPending.value = false;
                        break;
                    case KycStatus.NotVerified:
                    case KycStatus.Incomplete:
                    case KycStatus.Expired:
                        isStatusPending.value = false;
                        break;
                    case KycStatus.NeedsReview:
                        if (!hasCaptivePhoneNumber.value) {
                            freeFromCaptiveKyc({
                                shouldRedirect: true,
                            });
                            setModal(modalCodes.captiveKycPending);
                        }
                        break;
                    default:
                        isStatusPending.value = true;
                        break;
                }

                maybeRedirectToPhoneVerification();
            } catch (error) {
                if ((error as any).type === 'authenticationNeeded') {
                    freeFromCaptiveKyc({
                        shouldRedirect: true,
                    });
                    showSessionExpired();
                }
            }
        };

        const restartVerification = () => {
            AnalyticsSDK.getInstance().log('KYC_Unsuccessful_Click_Restart');
            startIdentityVerification(hasAttemptedKycThisView.value);
        };

        const interval = setInterval(async () => {
            if (isStatusPending.value === true) {
                await getKycData();
            }
        }, getAccountConfig().kycDetailsTimeCheck);

        onBeforeUnmount(() => {
            clearInterval(interval);
        });

        const { pushRoute } = useVueRouter();
        const callOnComplete = () => {
            closeModal({
                group: multiStepIdVerificationGroup.value!,
            });
            isStatusPending.value = true;

            maybeRedirectToPhoneVerification();
        };

        const maybeRedirectToPhoneVerification = () => {
            if ((isStatusPending.value || user.value?.kycDetails?.status === KycStatus.Verified) && shouldVerifySms.value) {
                pushRoute({ name: RouteName.App.VerifyPhoneCaptive });
            }
        };

        const skipKyc = () => {
            AnalyticsSDK.getInstance().log('KYC_Skip', {
                eventLabel: 'Registration',
            });
            pushRoute({ name: RouteName.App.Home });
        };

        const nextStepModalCode = getIntermediaryStep() || idVerificationModalCode.value;

        function startIdentityVerification(isFromFailed = false) {
            hasAttemptedKycThisView.value = true;
            setModal({
                code: nextStepModalCode,
                data: {
                    callOnComplete,

                },
                group: multiStepIdVerificationGroup.value || undefined,
            });

            if (isFromFailed) {
                AnalyticsSDK.getInstance().log('KYC_Unsuccessful_Click_Restart');
            } else {
                AnalyticsSDK.getInstance().log('KYC_Attempt', {
                    eventLabel: 'Registration',
                });
            }
        }

        return {
            callOnComplete,
            restartVerification,
            startIdentityVerification,
            skipKyc,
            isLoading,
            isStatusPending,
            isFailed,
            userKycStatus: user.value?.kycDetails?.status,
            shouldShowUnsuccessful,
            isUnskippable,
        };
    },
    components: {
        IdCardPendingKyc,
        VerificationPrompt,
        SdsButton,
    },
});
